import { ArrowLeftIcon, Loader2 } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  entityToTagMentionAttrs,
  getEntityTagDisplayName,
  isTagEntity,
  isTagEntityType,
  isTagUser,
  TagMentionAttrs,
  TagSuggestionItem,
} from "./types";

import { EntityIcon } from "@/components/ui/icons";
import { getEntityNumericId } from "@/queries";
import { EntityType } from "@/queries/folder-entities/types";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { getEntityColor } from "@/components/UnifiedRegistry/utils";
import { cn } from "@/lib/utils";

interface TagMentionListProps {
  items: TagSuggestionItem[];
  command: (params: TagMentionAttrs) => void;
  isLoading?: boolean;
  currentFolderId?: number | null;
  onFolderSelect?: (folderId: number | null) => void;
}

export interface TagMentionListHandle {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}

const CONTAINER_CLASSES =
  "min-w-[220px] max-w-[320px] rounded-md border border-gray-200 bg-white shadow-md";

const UserItem = ({
  item,
  isSelected,
  onClick,
  onMouseEnter,
  buttonRef,
}: {
  item: TagSuggestionItem & { type: "user" };
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) => (
  <button
    ref={buttonRef}
    type="button"
    className={cn(
      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
      "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
      isSelected && "bg-gray-100 text-gray-900",
    )}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    tabIndex={-1}
  >
    <span className="truncate font-medium text-blue-600">@{item.name}</span>
    {item.name !== item.email && (
      <span className="truncate text-xs text-gray-500">{item.email}</span>
    )}
  </button>
);

const EntityItem = ({
  item,
  isSelected,
  onClick,
  onMouseEnter,
  buttonRef,
}: {
  item: TagSuggestionItem & { type: "entity" };
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) => {
  const displayName = getEntityTagDisplayName(item.entity);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={cn(
        "flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left",
        "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
        isSelected && "bg-gray-100",
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      tabIndex={-1}
    >
      <div className={cn("flex-shrink-0 rounded-full p-1.5", getEntityColor(item.entity.type))}>
        <EntityIcon type={item.entity.type} size="xs" />
      </div>
      <OverflowDisplayTooltip content={displayName}>
        <span className="truncate text-xs font-medium">{displayName}</span>
      </OverflowDisplayTooltip>
    </button>
  );
};

const EntityTypeItem = ({
  item,
  isSelected,
  onClick,
  onMouseEnter,
  buttonRef,
}: {
  item: TagSuggestionItem & { type: "entityType" };
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) => (
  <button
    ref={buttonRef}
    type="button"
    className={cn(
      "flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left",
      "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
      isSelected && "bg-gray-100",
    )}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    tabIndex={-1}
  >
    <div className={cn("flex-shrink-0 rounded-full p-1.5", getEntityColor(item.entityType))}>
      <EntityIcon type={item.entityType} size="xs" />
    </div>
    <OverflowDisplayTooltip content={`All ${item.displayName}`}>
      <span className="truncate text-xs font-medium">All {item.displayName}</span>
    </OverflowDisplayTooltip>
  </button>
);

const TagMentionList = forwardRef<TagMentionListHandle, TagMentionListProps>((props, ref) => {
  const { workspaceId } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Use refs to always have access to current values in imperative handlers
  const itemsRef = useRef<TagSuggestionItem[]>(props.items);
  const selectedIndexRef = useRef(0);
  const commandRef = useRef(props.command);
  const currentFolderIdRef = useRef(props.currentFolderId ?? null);
  const onFolderSelectRef = useRef(props.onFolderSelect);

  // Keep refs in sync with props/state
  useEffect(() => {
    itemsRef.current = props.items;
  }, [props.items]);

  useEffect(() => {
    currentFolderIdRef.current = props.currentFolderId ?? null;
  }, [props.currentFolderId]);

  useEffect(() => {
    onFolderSelectRef.current = props.onFolderSelect;
  }, [props.onFolderSelect]);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    commandRef.current = props.command;
  }, [props.command]);

  // Reset selection when items or folder change
  useEffect(() => {
    setSelectedIndex(0);
    selectedIndexRef.current = 0;
    // Clear old refs when items change
    itemRefs.current = [];
  }, [props.items, props.currentFolderId]);

  const scrollToItem = useCallback((index: number) => {
    const container = containerRef.current;
    const element = itemRefs.current[index];

    if (!container || !element) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
      element.scrollIntoView({ block: "start", behavior: "smooth" });
    } else if (elementRect.bottom > containerRect.bottom) {
      element.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, []);

  const selectItem = useCallback(
    (index: number) => {
      const items = itemsRef.current;
      const currentFolderId = currentFolderIdRef.current;
      const onFolderSelect = onFolderSelectRef.current;

      const hasBackButton = currentFolderId !== undefined && currentFolderId !== null;
      const effectiveIndex = hasBackButton ? index - 1 : index;

      if (hasBackButton && index === 0) {
        onFolderSelect?.(null);
        return;
      }

      const item = items[effectiveIndex];
      if (!item) {
        return;
      }

      if (isTagEntity(item) && item.entity.type === EntityType.FOLDER && onFolderSelect) {
        onFolderSelect(getEntityNumericId(item.entity.id) ?? null);
        return;
      }

      const command = commandRef.current;
      if (isTagUser(item)) {
        command({
          tagType: "user",
          id: item.id,
          name: item.name,
          email: item.email,
        });
      } else if (isTagEntity(item)) {
        command(entityToTagMentionAttrs(item.entity, workspaceId));
      } else if (isTagEntityType(item)) {
        command({
          tagType: "entityType",
          name: item.displayName,
          entityType: item.entityType,
        });
      }
    },
    [workspaceId],
  );

  const getTotalItemCount = useCallback(() => {
    const items = itemsRef.current;
    const currentFolderId = currentFolderIdRef.current;
    const isInFolder = currentFolderId !== undefined && currentFolderId !== null;
    return isInFolder ? items.length + 1 : items.length;
  }, []);

  const upHandler = useCallback(() => {
    const totalItemCount = getTotalItemCount();
    if (totalItemCount === 0) {
      return;
    }
    const currentIndex = selectedIndexRef.current;
    const newIndex = (currentIndex + totalItemCount - 1) % totalItemCount;
    setSelectedIndex(newIndex);
    selectedIndexRef.current = newIndex;
    scrollToItem(newIndex);
  }, [scrollToItem, getTotalItemCount]);

  const downHandler = useCallback(() => {
    const totalItemCount = getTotalItemCount();
    if (totalItemCount === 0) {
      return;
    }
    const currentIndex = selectedIndexRef.current;
    const newIndex = (currentIndex + 1) % totalItemCount;
    setSelectedIndex(newIndex);
    selectedIndexRef.current = newIndex;
    scrollToItem(newIndex);
  }, [scrollToItem, getTotalItemCount]);

  const enterHandler = useCallback(() => {
    const items = itemsRef.current;
    if (items.length > 0) {
      selectItem(selectedIndexRef.current);
    }
  }, [selectItem]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          downHandler();
          return true;
        }

        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          enterHandler();
          return true;
        }

        if (event.key === "Escape") {
          return false;
        }

        return false;
      },
    }),
    [upHandler, downHandler, enterHandler],
  );

  const { items, isLoading } = props;

  // Group items by type
  const entityTypes = items.filter(isTagEntityType);
  const users = items.filter(isTagUser);
  const entities = items.filter(isTagEntity);
  const hasResults = items.length > 0;

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      );
    }

    if (!hasResults) {
      return (
        <div className="p-3">
          <span className="text-sm text-gray-500">No results found</span>
        </div>
      );
    }

    let itemIndex = 0;
    const { currentFolderId } = props;
    const isInFolder = currentFolderId !== undefined && currentFolderId !== null;

    return (
      <div className="p-1">
        {isInFolder && (
          <button
            ref={el => {
              itemRefs.current[0] = el;
            }}
            type="button"
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
              "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
              selectedIndex === 0 && "bg-gray-100 text-gray-900",
            )}
            onClick={() => selectItem(0)}
            onMouseEnter={() => {
              setSelectedIndex(0);
              selectedIndexRef.current = 0;
            }}
            tabIndex={-1}
          >
            <ArrowLeftIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
            <span className="truncate text-sm font-medium">Back to root</span>
          </button>
        )}
        {isInFolder && (entityTypes.length > 0 || users.length > 0 || entities.length > 0) && (
          <div className="my-1 border-t border-gray-100" />
        )}
        {entityTypes.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs font-semibold uppercase text-gray-400">
              Entity Types
            </div>
            {entityTypes.map(item => {
              const currentIndex = isInFolder ? itemIndex + 1 : itemIndex;
              itemIndex += 1;
              return (
                <EntityTypeItem
                  key={`entityType-${item.entityType}`}
                  item={item}
                  isSelected={selectedIndex === currentIndex}
                  onClick={() => selectItem(currentIndex)}
                  onMouseEnter={() => {
                    setSelectedIndex(currentIndex);
                    selectedIndexRef.current = currentIndex;
                  }}
                  buttonRef={el => {
                    itemRefs.current[currentIndex] = el;
                  }}
                />
              );
            })}
          </>
        )}

        {users.length > 0 && (
          <>
            {entityTypes.length > 0 && <div className="my-1 border-t border-gray-100" />}
            <div className="px-2 py-1 text-xs font-semibold uppercase text-gray-400">Users</div>
            {users.map(item => {
              const currentIndex = isInFolder ? itemIndex + 1 : itemIndex;
              itemIndex += 1;
              return (
                <UserItem
                  key={`user-${item.id}`}
                  item={item}
                  isSelected={selectedIndex === currentIndex}
                  onClick={() => selectItem(currentIndex)}
                  onMouseEnter={() => {
                    setSelectedIndex(currentIndex);
                    selectedIndexRef.current = currentIndex;
                  }}
                  buttonRef={el => {
                    itemRefs.current[currentIndex] = el;
                  }}
                />
              );
            })}
          </>
        )}

        {entities.length > 0 && (
          <>
            {(entityTypes.length > 0 || users.length > 0) && (
              <div className="my-1 border-t border-gray-100" />
            )}
            <div className="px-2 py-1 text-xs font-semibold uppercase text-gray-400">Entities</div>
            {entities.map(item => {
              const currentIndex = isInFolder ? itemIndex + 1 : itemIndex;
              itemIndex += 1;
              return (
                <EntityItem
                  key={`entity-${item.entity.id}-${item.entity.type}`}
                  item={item}
                  isSelected={selectedIndex === currentIndex}
                  onClick={() => selectItem(currentIndex)}
                  onMouseEnter={() => {
                    setSelectedIndex(currentIndex);
                    selectedIndexRef.current = currentIndex;
                  }}
                  buttonRef={el => {
                    itemRefs.current[currentIndex] = el;
                  }}
                />
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={cn(CONTAINER_CLASSES, "max-h-[280px] overflow-y-auto")}>
      {renderContent()}
    </div>
  );
});

TagMentionList.displayName = "TagMentionList";

export default TagMentionList;
